import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { API_URL } from '@config';
import { DEFAULT_CURRENCY_CODE } from '@constants/common';
import { FEATURE_FLAGS } from '@constants/feature';
import {
  AccountType,
  DEFAULT_PLACEHOLDER,
  OrgRoleType,
  PAGINATION_LIMIT,
  SPLIT_REQUIRED_AMOUNT_MIN
} from '@constants/generic';
import UserPermissionTypes from '@constants/permissions';
import { STORAGE_KEYS } from '@constants/storage';
import { useLazyGetAllBranchesQuery } from '@features/branch-management/queries';
import { useLazyGetAllGroupsWithoutPaginationQuery } from '@features/group-management/queries/GroupQuery';
import { getPaidOn, getSettledOn } from '@features/transactions/helper';
import { ActionsProps } from '@features/transactions/types';
import useCheckPermission from '@hooks/checkPermission';
import useNotify from '@hooks/notify';
import {
  MarkAsPaidOptionsInterface,
  MemberTransactionType,
  UpdateDueOptionsInterface,
  ValidationError
} from '@types';
import { formatISODate } from '@utils/date';
import { downloadFileFromUrl } from '@utils/downloadFile';
import {
  getAmountErrorMessage,
  getMaxAmountErrorMessage,
  sanitizeNumberInput
} from '@utils/generic';
import { getLocalStorageItem } from '@utils/storage';
import { ApiError } from 'types/api';
import { Branch } from 'types/branches';
import { DropdownOption } from 'types/dropdown';
import { Group } from 'types/groups';

import {
  useEnableSplitPaymentMutation,
  useGetLeadDetailsQuery,
  useLazyGetLeadPaymentOrdersQuery,
  useLeadClosePaymentMutation,
  useLeadDisablePaymentOrderMutation,
  useLeadDiscountMutation,
  useLeadMarkAsPaidMutation,
  useLeadQuickCollectMutation,
  useLeadSendPaymentReminderMutation
} from '../queries/LeadManagementQuery';

import {
  markAsPaidOptionsInitialState,
  updateDueOptionsInitialState
} from '../../transactions/constants';

const PAYMENT_MIN_AMOUNT = 2;
const PAYMENT_MAX_AMOUNT = 200000;
const NOTES_MIN_LENGTH = 3;

const usePaymentTab = (leadId?: string) => {
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const { accountType, orgFeatureFlags, rolesAndPermissions } = useSelector(
    (state: any) => state.user
  );
  const { createErrorAlert, createSuccessAlert } = useNotify();
  const { checkPermission } = useCheckPermission();

  // --- Modal & Form State ---
  const [isOpen, setIsOpen] = useState(false);
  const [branchesList, setBranchesList] = useState<Branch[]>([]);
  const [groupsList, setGroupsList] = useState<Group[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<DropdownOption | null>(
    null
  );
  const [selectedGroup, setSelectedGroup] = useState<DropdownOption | null>(
    null
  );
  const [amount, setAmount] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [formError, setFormError] = useState<ValidationError>({});

  // --- Payment List State ---
  const [paymentList, setPaymentList] = useState<any[]>([]);
  const [isPaymentListLoading, setIsPaymentListLoading] = useState(true);

  // --- Mark As Paid State ---
  const [markAsPaidOptions, setMarkAsPaidOptions] =
    useState<MarkAsPaidOptionsInterface>(markAsPaidOptionsInitialState);
  const [markAsPaidBranchId, setMarkAsPaidBranchId] = useState<string>('');

  // --- Send Reminder State ---
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [reminderPaymentRow, setReminderPaymentRow] =
    useState<MemberTransactionType | null>(null);

  // --- Disable Payment State ---
  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);
  const [disablePaymentRow, setDisablePaymentRow] =
    useState<MemberTransactionType | null>(null);

  // --- Update Due State ---
  const [updateDueOptions, setUpdateDueOptions] =
    useState<UpdateDueOptionsInterface>(updateDueOptionsInitialState);
  const [selectedUpdateDueRow, setSelectedUpdateDueRow] =
    useState<MemberTransactionType | null>(null);
  const [isDiscountConfirmationModalOpen, setIsDiscountConfirmationModalOpen] =
    useState<boolean>(false);
  const [discountData, setDiscountData] = useState<{
    discount_amount: string;
    note: string;
  } | null>(null);

  // --- Enable Partial Payment State ---
  const [isEnablePartialPaymentModalOpen, setIsEnablePartialPaymentModalOpen] =
    useState(false);
  const [partialPaymentRow, setPartialPaymentRow] =
    useState<MemberTransactionType | null>(null);

  // --- Payment Details Modal State ---
  const [isPaymentDetailsModalOpen, setIsPaymentDetailsModalOpen] =
    useState(false);
  const [selectedPaymentDetailsRow, setSelectedPaymentDetailsRow] =
    useState<MemberTransactionType | null>(null);

  // --- Pagination State ---
  const [page, setPage] = useState(1);

  // --- API Hooks ---
  const [getAllBranches] = useLazyGetAllBranchesQuery();
  const [getAllGroups] = useLazyGetAllGroupsWithoutPaginationQuery();
  const [getLeadPaymentOrders] = useLazyGetLeadPaymentOrdersQuery();
  const [leadQuickCollect, { isLoading: isLeadQuickCollectLoading }] =
    useLeadQuickCollectMutation();
  const [leadMarkAsPaid] = useLeadMarkAsPaidMutation();
  const [leadSendPaymentReminder] = useLeadSendPaymentReminderMutation();
  const [leadDisablePaymentOrder] = useLeadDisablePaymentOrderMutation();
  const [leadDiscount, { isLoading: isApplyingDiscount }] =
    useLeadDiscountMutation();
  const [enableSplitPayment, { isLoading: isEnablingSplitPayment }] =
    useEnableSplitPaymentMutation();
  const [leadClosePayment] = useLeadClosePaymentMutation();

  // --- Permissions ---
  const hasDownloadPaymentReportAccess = checkPermission([
    UserPermissionTypes.PAYMENT_ORDER_GENERATE_REPORT,
    UserPermissionTypes.LEAD_REPORT_GENERATE
  ]);

  // --- Lead Details ---
  const { data: leadDetails } = useGetLeadDetailsQuery(
    { organizationId: organizationId || '', leadId: leadId || '' },
    { skip: !organizationId || !leadId }
  );
  const { orgConfig } = useSelector((state: any) => state.user);

  const leadName = leadDetails?.name || '';
  const leadCountryCode = leadDetails?.country_code || '+91';
  const leadMobileNumber = leadDetails?.mobile_number || '';

  const splitRequiredAmountMin =
    orgConfig?.split_required_amount_min || SPLIT_REQUIRED_AMOUNT_MIN;

  // --- Data Fetching ---
  const handleGetAllBranches = async () => {
    const res = await getAllBranches({
      organizationId,
      queryString: `?currency_code=${DEFAULT_CURRENCY_CODE}`
    });
    if (res?.data) {
      setBranchesList(res.data?.data);
    }
  };

  const handleGetAllGroups = async (branchId: string) => {
    if (!branchId) return;
    const res = await getAllGroups({
      params: { organizationId, branchId },
      queryString: '?status=active'
    });
    if (res.data) {
      const groups = Array.isArray(res.data) ? res.data : res.data.data || [];
      setGroupsList(groups);
    } else if ('error' in res) {
      const error = res.error as ApiError;
      createErrorAlert(error.data?.message || 'Error in fetching groups');
    }
  };

  const fetchPaymentOrders = useCallback(async () => {
    if (!organizationId || !leadId) return;
    setIsPaymentListLoading(true);
    const res = await getLeadPaymentOrders({ organizationId, leadId }, false);
    if (res?.data) {
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setPaymentList(data);
    }
    setIsPaymentListLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, leadId]);

  // --- Form Helpers ---
  const resetForm = () => {
    setSelectedBranch(null);
    setSelectedGroup(null);
    setAmount('');
    setNotes('');
    setFormError({});
  };

  const handleStartCollectingPayment = () => {
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    resetForm();
  };

  const onAmountChange = (value: string) => {
    setFormError((prev) => ({ ...prev, amount: '' }));
    const sanitizedAmount = sanitizeNumberInput(value);
    if (Number(sanitizedAmount) > PAYMENT_MAX_AMOUNT) {
      setFormError((prev) => ({
        ...prev,
        amount: `Amount cannot be greater than ${PAYMENT_MAX_AMOUNT}`
      }));
    }
    setAmount(sanitizedAmount);
  };

  const onAmountBlur = (value: string) => {
    if (value.length && Number(value) < PAYMENT_MIN_AMOUNT) {
      setFormError((prev) => ({
        ...prev,
        amount: getAmountErrorMessage(PAYMENT_MIN_AMOUNT)
      }));
    }
    if (value.length && Number(value) > PAYMENT_MAX_AMOUNT) {
      setFormError((prev) => ({
        ...prev,
        amount: getMaxAmountErrorMessage(PAYMENT_MAX_AMOUNT)
      }));
    }
  };

  const onNotesChange = (value: string) => {
    setFormError((prev) => ({ ...prev, notes: '' }));
    setNotes(value);
  };

  const onNotesBlur = (value: string) => {
    if (value.length && value.length < NOTES_MIN_LENGTH) {
      setFormError((prev) => ({
        ...prev,
        notes: `Notes must be at least ${NOTES_MIN_LENGTH} characters`
      }));
    }
  };

  // --- Dropdown Options ---
  const branchListOptions: DropdownOption[] = useMemo(
    () =>
      (branchesList?.map((branch: Branch) => ({
        id: branch.id,
        value: branch.name,
        label: branch.name
      })) || []) as DropdownOption[],
    [branchesList]
  );

  const groupListOptions: DropdownOption[] = useMemo(
    () =>
      (groupsList?.map((group: Group) => ({
        id: group.id,
        value: group.name,
        label: group.name
      })) || []) as DropdownOption[],
    [groupsList]
  );

  // --- Send Payment Link ---
  const handleSendLink = async () => {
    if (!selectedBranch?.id) {
      createErrorAlert('Please select a branch');
      return;
    }
    if (!amount || Number(amount) < PAYMENT_MIN_AMOUNT) {
      setFormError((prev) => ({
        ...prev,
        amount: getAmountErrorMessage(PAYMENT_MIN_AMOUNT)
      }));
      return;
    }
    if (!notes || notes.length < NOTES_MIN_LENGTH) {
      setFormError((prev) => ({
        ...prev,
        notes: `Notes must be at least ${NOTES_MIN_LENGTH} characters`
      }));
      return;
    }

    const quickCollectBody = {
      amount: +amount,
      note: notes.trim(),
      ...(selectedGroup?.id && { group_id: selectedGroup.id as string }),
      member_details: [
        {
          name: leadName,
          mobile: leadMobileNumber,
          country_code: leadCountryCode.replace('+', '')
        }
      ],
      send_payment_links: true
    };

    const res = await leadQuickCollect({
      organizationId,
      leadId: leadId as string,
      branchId: selectedBranch.id as string,
      body: quickCollectBody
    });

    if ('data' in res) {
      createSuccessAlert('Payment link sent successfully');
      handleCloseModal();
      setTimeout(() => fetchPaymentOrders(), 1000);
    } else if ('error' in res) {
      const error = res.error as ApiError;
      createErrorAlert(
        error.data?.errors?.[0]?.message || 'Failed to send payment link'
      );
    }
  };

  const isSendDisabled =
    !selectedBranch ||
    !amount ||
    !notes ||
    !!formError.amount ||
    !!formError.notes ||
    Number(amount) < PAYMENT_MIN_AMOUNT ||
    notes.length < NOTES_MIN_LENGTH ||
    isLeadQuickCollectLoading;

  // --- Mark As Paid ---
  const getBranchId = (row: MemberTransactionType) => row.branch_id || '';

  const onClickMarkAsPaid = (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => {
    event.stopPropagation();
    setMarkAsPaidBranchId(getBranchId(row));
    setMarkAsPaidOptions({
      id: row.id,
      amount: row.total_amount || '',
      name: row.name,
      is_split_payment: row.is_split_payment,
      paid_amount: row.total_paid_amount
    });
  };

  const handleCustomMarkAsPaid = async (body: any) => {
    const res = await leadMarkAsPaid({
      organizationId,
      leadId: leadId as string,
      branchId: markAsPaidBranchId,
      paymentOrderId: markAsPaidOptions.id,
      body
    });
    return res;
  };

  const handleMarkAsPaidSuccess = () => {
    setMarkAsPaidOptions(markAsPaidOptionsInitialState);
    setMarkAsPaidBranchId('');
    fetchPaymentOrders();
  };

  // --- Send Reminder ---
  const onClickSendReminder = (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => {
    event.stopPropagation();
    setReminderPaymentRow(row);
    setIsReminderModalOpen(true);
  };

  const handleConfirmReminder = async () => {
    if (!reminderPaymentRow) return;
    const res = await leadSendPaymentReminder({
      organizationId,
      leadId: leadId as string,
      branchId: getBranchId(reminderPaymentRow),
      paymentOrderId: reminderPaymentRow.id,
      body: { payment_order_ids: [reminderPaymentRow.id] }
    });
    if ('data' in res) {
      createSuccessAlert('Reminder sent successfully');
      setIsReminderModalOpen(false);
      setReminderPaymentRow(null);
      fetchPaymentOrders();
    } else if ('error' in res) {
      const error = res.error as ApiError;
      createErrorAlert(error.data?.message || 'Failed to send reminder');
    }
  };

  const handleCancelReminder = () => {
    setIsReminderModalOpen(false);
    setReminderPaymentRow(null);
  };

  // --- Disable Payment ---
  const onClickDisable = (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => {
    event.stopPropagation();
    setDisablePaymentRow(row);
    setIsDisableModalOpen(true);
  };

  const handleConfirmDisable = async () => {
    if (!disablePaymentRow) return;
    const res = await leadDisablePaymentOrder({
      organizationId,
      leadId: leadId as string,
      branchId: getBranchId(disablePaymentRow),
      paymentOrderId: disablePaymentRow.id,
      body: { payment_order_ids: [disablePaymentRow.id] }
    });
    if ('data' in res) {
      createSuccessAlert('Transaction disabled successfully');
      setIsDisableModalOpen(false);
      setDisablePaymentRow(null);
      fetchPaymentOrders();
    } else if ('error' in res) {
      const error = res.error as ApiError;
      createErrorAlert(error.data?.message || 'Failed to disable payment');
    }
  };

  const handleCancelDisable = () => {
    setIsDisableModalOpen(false);
    setDisablePaymentRow(null);
  };

  // --- Enable Partial Payment ---
  const hasEnablePartialPaymentAccess = checkPermission([
    UserPermissionTypes.LEAD_MANAGEMENT_EDIT
  ]);

  const onClickEnablePartialPayment = (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => {
    event.stopPropagation();
    setPartialPaymentRow(row);
    setIsEnablePartialPaymentModalOpen(true);
  };

  const handleConfirmEnablePartialPayment = async () => {
    if (!partialPaymentRow || !leadId) return;
    const res = await enableSplitPayment({
      organizationId,
      leadId,
      branchId: partialPaymentRow.branch_id || '',
      paymentOrderId: partialPaymentRow.id
    });
    if ('data' in res) {
      setIsEnablePartialPaymentModalOpen(false);
      setPartialPaymentRow(null);
      createSuccessAlert('Enabled Partial Payment');
      fetchPaymentOrders();
    } else {
      setIsEnablePartialPaymentModalOpen(false);
      setPartialPaymentRow(null);
      const apiError = res.error as ApiError;
      createErrorAlert(
        apiError?.data?.message || 'Error enabling partial payment'
      );
    }
  };

  const handleCancelEnablePartialPayment = () => {
    setIsEnablePartialPaymentModalOpen(false);
    setPartialPaymentRow(null);
  };

  // --- Update Due ---
  const enableDiscountInUpdateDueModal = orgFeatureFlags?.includes(
    FEATURE_FLAGS.PAYMENT_ORDER_DISCOUNT
  );

  const onClickUpdateDue = (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => {
    event.stopPropagation();
    setSelectedUpdateDueRow(row);
    setUpdateDueOptions({
      id: row.id,
      total_fee: row.total_amount || '',
      total_paid: row.total_paid_amount || '',
      action: Number(row.total_paid_amount) > 0 ? 'close_payment' : 'discount',
      name: row.name
    });
  };

  const handleUpdateSelectedAction = (
    currentAction: 'close_payment' | 'discount'
  ) => {
    setUpdateDueOptions({
      ...updateDueOptions,
      action: currentAction
    });
  };

  const handleDueModalSuccessUpdate = (discountInfo?: {
    discount_amount: string;
    note: string;
  }) => {
    if (updateDueOptions.action === 'discount') {
      setDiscountData(discountInfo || null);
      setIsDiscountConfirmationModalOpen(true);
    } else {
      fetchPaymentOrders();
    }
    setUpdateDueOptions(updateDueOptionsInitialState);
  };

  const handleDiscountConfirmationButtonClick = async (
    notifyCustomer: boolean
  ) => {
    if (discountData && selectedUpdateDueRow) {
      const response = await leadDiscount({
        organizationId,
        leadId: leadId as string,
        branchId: getBranchId(selectedUpdateDueRow),
        paymentOrderId: selectedUpdateDueRow.id,
        body: {
          discount_amount: discountData.discount_amount,
          note: discountData.note,
          notify_customer: notifyCustomer
        }
      });
      if ('data' in response) {
        createSuccessAlert('Discount applied successfully');
        fetchPaymentOrders();
      } else {
        const apiError = response.error as ApiError;
        createErrorAlert(
          apiError?.data?.message || 'Error in applying discount'
        );
      }
    }
    setIsDiscountConfirmationModalOpen(false);
    setSelectedUpdateDueRow(null);
    setDiscountData(null);
  };

  const handleCancelUpdateDue = () => {
    setUpdateDueOptions(updateDueOptionsInitialState);
    setSelectedUpdateDueRow(null);
  };

  const handleCustomClosePayment = async (body: any) => {
    if (!selectedUpdateDueRow || !leadId) return { error: 'Missing data' };
    const res = await leadClosePayment({
      organizationId,
      leadId,
      branchId: getBranchId(selectedUpdateDueRow),
      paymentOrderId: selectedUpdateDueRow.id,
      body
    });
    return res;
  };

  // --- Copy Payment Link ---
  const handleCopyPaymentLink = useCallback(
    (event: React.MouseEvent, link: string) => {
      event.stopPropagation();
      navigator.clipboard.writeText(link);
      createSuccessAlert('Payment link copied');
    },
    [createSuccessAlert]
  );

  // --- Payment Details Modal ---
  const handleRowClick = useCallback(
    (payment: any) => {
      const details = paymentList.find(
        (row: MemberTransactionType) => row.id === payment.id
      );
      if (details) {
        setSelectedPaymentDetailsRow(details);
        setIsPaymentDetailsModalOpen(true);
      }
    },
    [paymentList]
  );

  const handleClosePaymentDetails = () => {
    setIsPaymentDetailsModalOpen(false);
    setSelectedPaymentDetailsRow(null);
  };

  // eslint-disable-next-line no-nested-ternary
  const downloadReciptId = selectedPaymentDetailsRow
    ? selectedPaymentDetailsRow.is_split_payment &&
      selectedPaymentDetailsRow.split_payment_order_id
      ? selectedPaymentDetailsRow.split_payment_order_id
      : selectedPaymentDetailsRow.id
    : '';

  const handlePaymentDetailsDownload = () => {
    if (selectedPaymentDetailsRow) {
      downloadFileFromUrl(
        `${API_URL}/p/${downloadReciptId}/download?split-order=${selectedPaymentDetailsRow.is_split_payment}`,
        `payment-receipt-${downloadReciptId}`,
        true
      );
    }
  };

  const showUpdateDue = (row: MemberTransactionType) =>
    enableDiscountInUpdateDueModal &&
    !(
      Number(row.total_paid_amount) === 0 &&
      Number(row.total_amount) - Number(row.total_paid_amount) < 100
    );

  // --- Feature Flag & Role ---
  const isRestrictDisablePaymentOrderEnabled =
    orgFeatureFlags?.includes(FEATURE_FLAGS.RESTRICT_DISABLE_PAYMENT_ORDER) ??
    false;

  const isOwnerOrOrgAdmin =
    rolesAndPermissions?.role === OrgRoleType.OWNER ||
    rolesAndPermissions?.role === OrgRoleType.ORGANISATION_ADMIN ||
    rolesAndPermissions?.role === OrgRoleType.INDIVIDUAL;

  // --- Actions ---
  const getPaymentActions = useCallback(
    (row: MemberTransactionType): ActionsProps[] => {
      const isRowDisabled = row.status === 'disabled';
      const isProcessing = row.status === 'processing';
      const showDisable =
        isProcessing &&
        row.total_paid_amount === '0' &&
        (!isRestrictDisablePaymentOrderEnabled || isOwnerOrOrgAdmin);

      return [
        {
          id: `markAsPaid-${row.id}`,
          label: 'Mark As Paid',
          onClick: (event: React.MouseEvent) => onClickMarkAsPaid(event, row),
          isDisabled: isRowDisabled,
          style: 'text-primary'
        },
        {
          id: `sendReminder-${row.id}`,
          label: !row.remind_again ? 'Reminder Sent' : 'Send Reminder',
          onClick: (event: React.MouseEvent) => onClickSendReminder(event, row),
          isDisabled: isRowDisabled || !row.remind_again
        },
        ...(isProcessing && enableDiscountInUpdateDueModal && showUpdateDue(row)
          ? [
              {
                id: `updateDue-${row.id}`,
                label: 'Update Due Amount',
                onClick: (event: React.MouseEvent) =>
                  onClickUpdateDue(event, row),
                isDisabled: isRowDisabled
              }
            ]
          : []),
        ...(isProcessing &&
        hasEnablePartialPaymentAccess &&
        !row.is_split_payment &&
        !(Number(row.amount) < Number(splitRequiredAmountMin))
          ? [
              {
                id: `enablePartialPayment-${row.id}`,
                label: 'Enable Partial Payment',
                onClick: (event: React.MouseEvent) =>
                  onClickEnablePartialPayment(event, row),
                isDisabled: isRowDisabled,
                style: 'text-yellow60'
              }
            ]
          : []),
        ...(row?.payment_link
          ? [
              {
                id: `copyLink-${row.id}`,
                label: 'Copy Payment Link',
                onClick: (event: React.MouseEvent) =>
                  handleCopyPaymentLink(event, row.payment_link),
                iconName: 'copy-primary',
                tooltipText: 'Copied!',
                isDisabled: isRowDisabled
              }
            ]
          : []),
        ...(showDisable
          ? [
              {
                id: `disable-${row.id}`,
                label: 'Disable',
                onClick: (event: React.MouseEvent) =>
                  onClickDisable(event, row),
                isDisabled: isRowDisabled,
                style: 'text-error40'
              }
            ]
          : [])
      ];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const getDownloadActions = useCallback(
    (row: any): ActionsProps[] => [
      {
        ...(hasDownloadPaymentReportAccess && {
          id: `download-${row.id}`,
          label: 'Download Receipt',
          onClick: (event: React.MouseEvent) => {
            event.stopPropagation();
            downloadFileFromUrl(
              `${API_URL}/p/${
                row.is_split_payment ? row.split_payment_order_id : row.id
              }/download?split-order=${row.is_split_payment}`,
              `payment-receipt-${row.id}`,
              true
            );
          }
        })
      }
    ],
    [hasDownloadPaymentReportAccess]
  );

  // --- Table Data ---
  const disabledRowIds = useMemo(
    () =>
      paymentList
        .filter((row: any) => row.status === 'disabled')
        .map((row: any) => row.id),
    [paymentList]
  );

  const modifiedTableData = useMemo(
    () =>
      paymentList.map((row: any) => ({
        id: row.id,
        branch: row.branch_name || DEFAULT_PLACEHOLDER,
        groupNotes: row.group_name || row.note || DEFAULT_PLACEHOLDER,
        requestedOn: row.due_at
          ? formatISODate({ date: row.due_at })
          : DEFAULT_PLACEHOLDER,
        paidOn: getPaidOn(
          row.id,
          row.paid_at || '',
          row.split_payment_order_details
        ),
        settledOn: getSettledOn(
          row.id,
          row.status,
          row.settlement_status,
          row.payment_mode,
          row.settled_at,
          row.settlement_details
        ),
        amount: row,
        actions: { row, status: row.status }
      })),
    [paymentList]
  );

  const totalCount = modifiedTableData.length;

  const paginatedTableData = useMemo(() => {
    const startIndex = (page - 1) * PAGINATION_LIMIT;
    return modifiedTableData.slice(startIndex, startIndex + PAGINATION_LIMIT);
  }, [modifiedTableData, page]);

  const hasPaymentData = paymentList.length > 0;

  // --- Effects ---
  useEffect(() => {
    fetchPaymentOrders();
  }, [fetchPaymentOrders]);

  useEffect(() => {
    if (accountType?.type === AccountType.ORGANIZATION && isOpen) {
      handleGetAllBranches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (selectedBranch?.id) {
      setSelectedGroup(null);
      setGroupsList([]);
      handleGetAllGroups(selectedBranch.id as string);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  return {
    // Modal & Form
    isOpen,
    selectedBranch,
    setSelectedBranch,
    selectedGroup,
    setSelectedGroup,
    amount,
    notes,
    formError,
    branchListOptions,
    groupListOptions,
    onAmountChange,
    onAmountBlur,
    onNotesChange,
    onNotesBlur,
    handleStartCollectingPayment,
    handleCloseModal,
    handleSendLink,
    isSendDisabled,
    isLeadQuickCollectLoading,

    // Lead Details
    leadName,
    leadCountryCode,
    leadMobileNumber,

    // Payment List
    paymentList,
    isPaymentListLoading,
    hasPaymentData,
    paginatedTableData,
    totalCount,
    disabledRowIds,
    getPaymentActions,
    getDownloadActions,

    // Pagination
    page,
    setPage,

    // Mark As Paid
    markAsPaidOptions,
    setMarkAsPaidOptions,
    markAsPaidBranchId,
    handleCustomMarkAsPaid,
    handleMarkAsPaidSuccess,

    // Send Reminder
    isReminderModalOpen,
    handleConfirmReminder,
    handleCancelReminder,

    // Disable Payment
    isDisableModalOpen,
    handleConfirmDisable,
    handleCancelDisable,

    // Enable Partial Payment
    isEnablePartialPaymentModalOpen,
    handleConfirmEnablePartialPayment,
    handleCancelEnablePartialPayment,
    isEnablingSplitPayment,

    // Update Due
    updateDueOptions,
    handleCancelUpdateDue,
    handleUpdateSelectedAction,
    handleDueModalSuccessUpdate,
    handleCustomClosePayment,
    isDiscountConfirmationModalOpen,
    handleDiscountConfirmationButtonClick,
    isApplyingDiscount,
    selectedUpdateDueRow,

    // Payment Details Modal
    isPaymentDetailsModalOpen,
    handleRowClick,
    handleClosePaymentDetails,
    handlePaymentDetailsDownload,
    selectedPaymentDetailsRow,

    // Constants
    PAYMENT_MIN_AMOUNT,
    PAYMENT_MAX_AMOUNT,

    // Misc
    organizationId
  };
};

export default usePaymentTab;
